import {
  formatMoney,
  formatNumber,
  formatPercent,
  formatPeriodStart,
  toThousandths,
  type ChartPoint,
  type ChartSeries,
} from '@platform/design-system';
import type { ValueScaleKind } from './series-ticks.ts';
import type { BreakdownMeasure, BreakdownReport } from './widget-queries.ts';

type BreakdownValue = string | null;

const NO_VALUE = '—';

export const scaleOfMeasure = (measure: BreakdownMeasure): ValueScaleKind => {
  if (measure === 'sales') return 'money';
  if (measure === 'deliveryRate') return 'ratio';
  return 'count';
};

const NUMBERS: Readonly<Record<ValueScaleKind, (value: string) => number>> = {
  money: toThousandths,
  count: Number,
  ratio: Number,
};

export type ValueDisplay = { readonly locale: string; readonly currency: string };

const DISPLAYS: Readonly<Record<ValueScaleKind, (value: string, display: ValueDisplay) => string>> = {
  money: (value, display) => formatMoney({ amount: value, currency: display.currency }, display.locale),
  count: (value, display) => formatNumber(Number(value), display.locale),
  ratio: (value, display) => formatPercent(Number(value), display.locale),
};

export const pointValueOf = (value: BreakdownValue, scale: ValueScaleKind): number =>
  value === null ? 0 : NUMBERS[scale](value);

export const displayOf = (value: BreakdownValue, scale: ValueScaleKind, display: ValueDisplay): string =>
  value === null ? NO_VALUE : DISPLAYS[scale](value, display);

export type KeyNamer = (key: string) => string;

export const breakdownSeriesOf = (
  report: BreakdownReport,
  nameOf: KeyNamer,
  locale: string,
): readonly ChartSeries[] => {
  const scale = scaleOfMeasure(report.measure);
  const display = { locale, currency: report.currency };
  return report.keys
    .map(({ key }) => ({
      key,
      label: nameOf(key),
      points: report.series.map((point) => {
        const value = point.values[key] ?? null;
        return {
          key: point.start,
          label: formatPeriodStart(point.start, locale, report.unit, report.timeZone),
          value: pointValueOf(value, scale),
          display: displayOf(value, scale, display),
          ...(value === null ? { missing: true as const } : {}),
        };
      }),
    }))
    .filter((series) => series.points.some((point) => point.missing !== true));
};

export const breakdownTotalsOf = (
  report: BreakdownReport,
  nameOf: KeyNamer,
  describe: { readonly locale: string; readonly versus: (value: string, previous: string) => string },
): readonly ChartPoint[] => {
  const scale = scaleOfMeasure(report.measure);
  const display = { locale: describe.locale, currency: report.currency };
  const previous = new Map((report.comparison?.keys ?? []).map((total) => [total.key, total.value]));
  return report.keys.map(({ key, value }) => {
    const current = displayOf(value, scale, display);
    return {
      key,
      label: nameOf(key),
      value: pointValueOf(value, scale),
      display: previous.has(key)
        ? describe.versus(current, displayOf(previous.get(key) ?? null, scale, display))
        : current,
    };
  });
};
